import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download } from 'lucide-react';
import heroImage from '../../assests/hero-image.png';
import resumePdf from '../../assests/myResume.pdf';

export default function Hero() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <section
        id="hero"
        className="relative min-h-screen flex items-center overflow-hidden pt-20"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 w-full relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 items-center min-h-[calc(100vh-5rem)]">

            {/* Left — Text Content */}
            <div className="flex flex-col justify-center relative z-10 pl-0 sm:pl-4 lg:pl-12 pt-[40vh] sm:pt-[45vh] lg:pt-0 pb-10 lg:pb-0">

              {/* Name */}
              <motion.h1
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15 }}
                className="font-bebas text-white leading-none mb-6 text-center lg:text-left"
                style={{
                  fontSize: 'clamp(4rem, 12vw, 9rem)',
                  letterSpacing: '0.01em',
                }}
              >
                MD TOUFIQUE
              </motion.h1>

              {/* Subtitle */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                className="mb-8 lg:mb-10 text-center lg:text-left"
              >
                <h2
                  className="text-white font-bebas leading-[1.1]"
                  style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)' }}
                >
                  I am a Full Stack AI<br className="hidden lg:block" />
                  Engineer
                </h2>
              </motion.div>

              {/* Resume Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="relative inline-block flex justify-center lg:justify-start"
              >
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="relative flex items-center gap-2 px-6 py-2.5 rounded-full bg-black border border-[#00B92E] text-[#00B92E] font-bold tracking-[0.1em] uppercase hover:bg-[#00B92E] hover:text-black transition-all duration-300 shadow-[0_0_10px_rgba(0,185,46,0.3)] hover:shadow-[0_0_15px_rgba(0,185,46,0.5)]"
                  style={{ fontSize: '0.9rem', fontFamily: 'Orbitron, sans-serif' }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                  View Resume
                </button>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Right — Ben 10 Hero Image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="absolute top-0 right-0 w-full lg:w-[65%] xl:w-[60%] h-[55vh] lg:h-full pointer-events-none z-0 flex items-start lg:items-center justify-center lg:justify-end pt-10 lg:pt-0"
        >
          <motion.img
            src={heroImage}
            alt="MD TOUFIQUE — Ben 10 Avatar"
            className="w-[85%] sm:w-[65%] lg:w-full h-full lg:h-auto object-contain object-top lg:object-right drop-shadow-2xl opacity-90 mix-blend-screen"
            animate={{ y: [-8, 8, -8] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          />
        </motion.div>
      </section>

      {/* Resume Preview Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
          >
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={() => setIsModalOpen(false)}
            />

            {/* Modal Content */}
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="relative w-full max-w-5xl h-[85vh] bg-[#111] border border-[#00B92E]/30 rounded-2xl overflow-hidden shadow-[0_0_50px_rgba(0,185,46,0.15)] flex flex-col"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-3 sm:p-4 border-b border-white/10 bg-black/50">
                <h3 className="text-white font-bold tracking-wider flex items-center gap-2 text-sm sm:text-base" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  <span className="w-2 h-2 rounded-full bg-[#00B92E] animate-pulse shrink-0" />
                  <span className="truncate">Resume Preview</span>
                </h3>
                
                <div className="flex items-center gap-2 sm:gap-4 shrink-0">
                  <a
                    href={resumePdf}
                    download="MD_TOUFIQUE_Resume.pdf"
                    className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-[#00B92E]/20 text-[#00B92E] rounded-full text-xs sm:text-sm font-medium hover:bg-[#00B92E] hover:text-black transition-colors"
                  >
                    <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Download PDF</span>
                    <span className="sm:hidden">Download</span>
                  </a>
                  <button 
                    onClick={() => setIsModalOpen(false)}
                    className="p-1.5 sm:p-2 text-white/50 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                  >
                    <X className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </div>

              {/* Modal Body (PDF Viewer) */}
              <div className="flex-1 w-full bg-white relative">
                {/* Fallback overlay if object fails on mobile */}
                <div className="absolute inset-0 flex items-center justify-center bg-[#1a1a1a] pointer-events-none hidden max-md:flex flex-col gap-4 text-center p-6">
                  <svg viewBox="0 0 24 24" fill="none" stroke="#00B92E" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="w-16 h-16 opacity-50">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                  <p className="text-white/70">Mobile browsers may not support inline PDF preview.</p>
                  <a
                    href={resumePdf}
                    download="MD_TOUFIQUE_Resume.pdf"
                    className="px-6 py-2 bg-[#00B92E] text-black font-bold rounded-full mt-2 pointer-events-auto"
                  >
                    Download Instead
                  </a>
                </div>

                <object
                  data={resumePdf}
                  type="application/pdf"
                  className="w-full h-full relative z-10"
                >
                  <iframe 
                    src={resumePdf} 
                    className="w-full h-full border-none"
                    title="Resume Preview"
                  >
                    <p>Your browser does not support PDFs. <a href={resumePdf} download>Download the PDF</a>.</p>
                  </iframe>
                </object>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}